import { forwardRef, useEffect, useRef, useState } from "react";

interface Props {
  /** Replay URL (player) or HTML (editor, rendered via srcdoc). */
  src?: string;
  srcDoc?: string;
  /** The viewport size the page was captured at, in CSS pixels. */
  width: number;
  height: number;
  /** Let the page receive hover (player). Off where hotspot drawing sits on top. */
  interactive?: boolean;
  /** Editor only: parent needs the document, so the frame shares our origin (scripts stay blocked). */
  sameOrigin?: boolean;
  /** Player: the page scrolls. `src` must be the ?scroll=1 replay, which runs one fixed script of ours
   *  (in an opaque-origin sandbox) that reports anchor positions to the parent by message. */
  scrollable?: boolean;
  /** When true, locks the iframe viewport and disables scrolling inside the replay. */
  scrollLocked?: boolean;
  title?: string;
  onLoad?: () => void;
  "data-qa-old"?: boolean;
}

/**
 * Renders a captured page at its original viewport size, scaled to fill its box. That box is the
 * coordinate origin for everything laid over the page: hotspots are percentages of it, in the
 * editor, on the Hotspots step and in the player alike, so an overlay must be a positioned sibling
 * filling the same parent, never nested inside a differently sized wrapper. The frame is
 * sandboxed with no scripts, so a capture can show hover states and real text but never run code.
 * (The one exception is the player's `scrollable` variant, which runs a single fixed script of ours.)
 */
const CaptureFrame = forwardRef<HTMLIFrameElement, Props>(function CaptureFrame(
  { src, srcDoc, width, height, interactive = true, sameOrigin = false, scrollable = false, scrollLocked = false, title = "Captured page", onLoad, "data-qa-old": isOld }, ref,
) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const localIframeRef = useRef<HTMLIFrameElement | null>(null);

  const setIframeRef = (el: HTMLIFrameElement | null) => {
    localIframeRef.current = el;
    if (typeof ref === "function") {
      ref(el);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLIFrameElement | null>).current = el;
    }
  };

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    // contentRect is fractional; clientWidth rounds to a whole pixel, which would leave the page up to
    // half a pixel narrower than the hotspot boxes laid over it (they are % of this same box).
    const ro = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  useEffect(() => {
    const win = localIframeRef.current?.contentWindow;
    if (win) {
      win.postMessage({ ihReplay: "lockScroll", locked: scrollLocked }, "*");
    }
  }, [scrollLocked]);

  const handleLoad = () => {
    if (scrollLocked) {
      localIframeRef.current?.contentWindow?.postMessage({ ihReplay: "lockScroll", locked: true }, "*");
    }
    onLoad?.();
  };

  return (
    <div
      ref={box}
      className={`capture-frame ${scrollLocked ? "is-scroll-locked" : ""}`}
      style={{
        aspectRatio: `${width} / ${height}`,
        overflow: scrollLocked ? "hidden" : undefined,
      }}
    >
      <iframe
        ref={setIframeRef}
        title={title}
        src={src}
        srcDoc={srcDoc}
        {...(isOld ? { "data-qa-old": true } : {})}
        sandbox={sameOrigin ? "allow-same-origin" : (scrollable || scrollLocked) ? "allow-scripts" : ""}
        referrerPolicy="no-referrer"
        loading="eager"
        tabIndex={-1}
        scrolling={scrollLocked ? "no" : (scrollable ? undefined : "no")}
        onLoad={handleLoad}
        style={{
          width, height, transform: `scale(${scale})`, visibility: scale ? "visible" : "hidden",
          pointerEvents: interactive ? "auto" : "none",
          overflow: scrollLocked ? "hidden" : undefined,
          touchAction: scrollLocked ? "none" : undefined,
        }}
      />
    </div>
  );
});
export default CaptureFrame;
